import React, { useEffect, useState, useCallback } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment-timezone';
import { getCotizacionData, enviarCotizacion } from './services/';

import ErrorPage from './components/ErrorPage';
import SuccessPage from './components/SuccessPage';
import ItemForm from './components/ItemForm';
import { ConfirmEmptyItemModal } from './components/ConfirmEmptyItemModal';
import { CotizacionData, OfertaData, ItemOfertaData } from './types';
import { isItemFilled, validarItemIndividual } from './functions';

/**
 * Extrae los parámetros de la URL
 */
function getQueryParams(): { pedido: string | null; proveedor: string | null; mode: string | null } {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    pedido: urlParams.get('pedido'),
    proveedor: urlParams.get('proveedor'),
    mode: urlParams.get('mode'),
  };
}

const App = () => {
  const [data, setData] = useState<CotizacionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [confirmEmptyItemOpen, setConfirmEmptyItemOpen] = useState(false);
  const [successInfo, setSuccessInfo] = useState<{ fechaHora: string; nombreProveedor: string } | null>(null);

  // Inicializar dataOferta con estructura base
  const [dataOferta, setDataOferta] = useState<OfertaData>({
    codigo_pedido: '',
    codigo_proveedor: '',
    fecha_oferta: '',
    moneda: '',
    items_oferta: [],
    empresa_compradora: '',
    empresa_vendedora: '',
    grupo_compra: '',
  });

  useEffect(() => {
    // Obtener parámetros de la URL
    const { pedido, proveedor } = getQueryParams();
    
    // Validar que los parámetros estén presentes para el modo normal
    if (!pedido || !proveedor) {
      setError('Parámetros requeridos no encontrados: pedido y proveedor son obligatorios');
      setLoading(false);
      return;
    }
    
    getCotizacionData(pedido, proveedor)
      .then((result: unknown) => {
        let cotizacionData: CotizacionData | null = null;
        if (result && Array.isArray(result) && result.length > 0) {
          cotizacionData = result[0] as CotizacionData;
        } else if (result && !Array.isArray(result)) {
          cotizacionData = result as CotizacionData;
        } else {
          setError(
            'No se pudo obtener datos válidos de la API (respuesta no es JSON).',
          );
          return;
        }
        setData(cotizacionData);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // Actualizar dataOferta cuando se obtenga data de la API
  useEffect(() => {
    if (data?.items) {
      const initialItems = data.items.map((item) => ({
        codigo_item: String(item.numeroItem),
        codigo_producto: item.codigoProducto,
        unidad_medida: item.unidadMedida,
        moneda: item.moneda,
        orden_compra: item.ordenCompra,
        item_orden_compra: item.itemOrdenCompra,
        codigo_pedido: String(data.codigoPedido),
        tipo_entrega: '',
        observaciones: '',
        valido_hasta: '', // Campo independiente para "Válido Hasta"
        precio_neto: 0,
        precio_total: 0,
        descuento: 0,
        cantidad: item.totalUnidadesSolicitadas,
        precio_bruto: 0,
        fecha_entrega: '',
        dias_pp: '',
        descuento_pp: '',
      }));

      setDataOferta({
        codigo_pedido: String(data.codigoPedido),
        codigo_proveedor: data.codigoProveedor,
        fecha_oferta: '',
        moneda: data.items.length > 0 ? data.items[0].moneda : '',
        items_oferta: initialItems,
        empresa_compradora: data.empresa_compradora ?? '',
        empresa_vendedora: data.empresa_vendedora ?? '',
        grupo_compra: data.grupo_compra ?? '',
      });
    }
  }, [data]);

  // Función para resetear todos los items a sus valores iniciales
  const resetAllItems = useCallback(() => {
    if (!data?.items) return;

    setDataOferta((prev) => ({
      ...prev,
      items_oferta: prev.items_oferta.map((itemOferta, index) => {
        const originalItem = data.items[index];
        if (!originalItem) return itemOferta;

        // Resetear todos los campos editables a sus valores iniciales
        return {
          ...itemOferta,
          // Mantener los campos que no son editables
          codigo_item: String(originalItem.numeroItem),
          codigo_producto: originalItem.codigoProducto,
          unidad_medida: originalItem.unidadMedida,
          moneda: originalItem.moneda,
          codigo_pedido: String(data.codigoPedido),
          // Resetear campos editables a valores iniciales
          tipo_entrega: '',
          observaciones: '',
          valido_hasta: '', // Resetear campo valido_hasta
          precio_neto: 0,
          precio_total: 0,
          descuento: 0,
          cantidad: originalItem.totalUnidadesSolicitadas, // Restaurar cantidad original
          precio_bruto: 0,
          fecha_entrega: '',
          dias_pp: '',
          descuento_pp: '',
        };
      }),
    }));
  }, [data]);

  // Función para obtener la fecha de oferta
  const getFechaOferta = useCallback((): string => {
    // Buscar la primera fecha "Válido Hasta" en los items (usar campo valido_hasta)
    for (const item of dataOferta.items_oferta) {
      if (item.valido_hasta && item.valido_hasta.trim() !== '') {
        return item.valido_hasta;
      }
    }
    // Si no hay fecha en los items, usar fechaMaxima del pedido
    if (data?.fechaMaxima) {
      return moment(data.fechaMaxima).format('YYYY-MM-DD');
    }
    return '';
  }, [dataOferta, data]);

  // Función para enviar la cotización
  const handleEnviarCotizacion = useCallback(async (itemsToSend: ItemOfertaData[]) => {
    const { pedido } = getQueryParams();
    if (!pedido) {
      setSnackbarMessage({ type: 'error', text: 'No se pudo obtener el código del pedido' });
      setSnackbarOpen(true);
      return;
    }

    setIsSending(true);

    try {
      const fechaOferta = getFechaOferta();
      const ofertaToSend: OfertaData = {
        ...dataOferta,
        fecha_oferta: fechaOferta,
        items_oferta: itemsToSend,
      };

      const result = await enviarCotizacion(ofertaToSend, pedido);
      
      if (result.success) {
        // Limpiar todos los campos de todos los items cuando el envío sea exitoso
        resetAllItems();
        // Redirigir a página de éxito con fecha/hora y nombre del proveedor
        setSuccessInfo({
          fechaHora: moment().format('DD MMM YYYY - H:mm'),
          nombreProveedor: data?.nombreProveedor ?? '',
        });
      } else {
        setSnackbarMessage({ 
          type: 'error', 
          text: result.message ?? 'Error al enviar la cotización' 
        });
        setSnackbarOpen(true);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      setSnackbarMessage({ type: 'error', text: `Error al enviar cotización: ${errorMessage}` });
      setSnackbarOpen(true);
    } finally {
      setIsSending(false);
    }
  }, [data, dataOferta, getFechaOferta, resetAllItems]);

  // Función para manejar el click del botón enviar
  const handleEnviarClick = useCallback(() => {
    // Filtrar items llenos y vacíos
    const filledItems = dataOferta.items_oferta.filter(item => isItemFilled(item));
    const emptyItems = dataOferta.items_oferta.filter(item => !isItemFilled(item));
    
    // Si hay al menos un item lleno
    if (filledItems.length > 0) {
      // Si también hay items vacíos, mostrar modal de confirmación
      if (emptyItems.length > 0) {
        setConfirmEmptyItemOpen(true);
      } else {
        // Si solo hay items llenos, enviar directamente
        handleEnviarCotizacion(filledItems);
      }
      return;
    }

    // Si no hay items llenos, no se puede enviar la cotización totalmente vacía
    // Se deben hacer las validaciones de formulario para mostrar los errores
    if (emptyItems.length > 0) {
      // Validar el primer item vacío para mostrar los errores de validación
      const firstEmptyItem = emptyItems[0];
      const validationError = validarItemIndividual(firstEmptyItem, data);
      
      if (validationError) {
        // Mostrar mensaje de error de validación
        const errorMessage = validationError.split('|FOCUS:')[0];
        setSnackbarMessage({ 
          type: 'error', 
          text: `No se puede enviar una cotización totalmente vacía. Debe completar al menos un item. ${errorMessage}` 
        });
        setSnackbarOpen(true);
      } else {
        // Si no hay error de validación (caso poco probable), mostrar mensaje genérico
        setSnackbarMessage({ 
          type: 'error', 
          text: 'No se puede enviar una cotización totalmente vacía. Debe completar al menos un item.' 
        });
        setSnackbarOpen(true);
      }
    } else {
      setSnackbarMessage({ 
        type: 'error', 
        text: 'No hay items para enviar' 
      });
      setSnackbarOpen(true);
    }
  }, [dataOferta, handleEnviarCotizacion, data]);

  // Función para confirmar envío de item vacío
  const handleConfirmEmptyItem = useCallback(() => {
    // Obtener solo los items llenos (completamente llenos)
    const filledItems = dataOferta.items_oferta.filter(item => isItemFilled(item));
    
    // Solo enviar los items completamente llenos, no los items vacíos
    handleEnviarCotizacion(filledItems);
    
    setConfirmEmptyItemOpen(false);
  }, [dataOferta, handleEnviarCotizacion]);

  // Función para cancelar envío de item vacío
  const handleCancelEmptyItem = useCallback(() => {
    setConfirmEmptyItemOpen(false);
  }, []);

  if (error) {
    return <ErrorPage error={error} />;
  }

  if (successInfo) {
    return <SuccessPage nombreProveedor={successInfo.nombreProveedor} fechaHora={successInfo.fechaHora} />;
  }

  let content;
  if (loading) {
    content = <Typography>Cargando datos...</Typography>;
  } else if (data?.items) {
    // Crear un mapa de índice para evitar usar findIndex dentro del map
    // Esto resuelve el issue de SonarQube sobre no usar índices de array
    const itemIndexMap = new Map<string, number>();
    data.items.forEach((item, index) => {
      const uniqueKey = `${item.codigoProducto}-${item.numeroItem}`;
      itemIndexMap.set(uniqueKey, index);
    });

    // Calcular si hay items llenos para determinar si se deben validar los items vacíos
    const hasFilledItems = dataOferta.items_oferta.some(item => isItemFilled(item));
    // Solo validar items vacíos si no hay ningún item lleno
    const shouldValidate = !hasFilledItems;

    content = (
      <>
        {data.items.map((item) => {
          // Usar una combinación única de propiedades del item para la key
          // No usar el índice del array directamente en la key
          const uniqueKey = `${item.codigoProducto}-${item.numeroItem}`;
          // Obtener el índice del mapa pre-construido
          const itemIndex = itemIndexMap.get(uniqueKey) ?? 0;
          
          return (
            <ItemForm
              key={uniqueKey}
              item={item}
              itemIndex={itemIndex}
              dataOferta={dataOferta}
              setDataOferta={setDataOferta}
              parentData={data}
              shouldValidate={shouldValidate}
            />
          );
        })}
      </>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>
        {/* Header */}
        <AppBar position="static" sx={{ bgcolor: '#003C82' }}>
          <Toolbar>
            <Box
              component="img"
              src="logo-coordinadora.svg"
              alt="Logo Coordinadora"
              sx={{
                width: 'auto',
                height: 60,
                mr: 2,
                display: 'block',
              }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              AUTOCOTIZADOR
            </Typography>
            <Box sx={{ bgcolor: '#1063AC', px: 2, py: 0.5, borderRadius: 1 }}>
              <Typography variant="body2" color="white">
                AUTOCOTIZACIÓN
              </Typography>
              <Typography variant="body2" color="white">
                N° {data ? data.codigoPedido : ''}
              </Typography>
              <Typography variant="caption" color="white">
                {data ? data.fechaPedido : ''}
              </Typography>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Avisos */}
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="textSecondary">
            - Te sugerimos que utilices el <b>navegador Chrome</b>.
            <br />
            - <b>Ten en cuenta</b> que los valores suministrados son{' '}
            <b>POR UNIDAD y Antes de impuestos</b>
            <br />
            - Los campos con (*) son obligatorios para diligenciar.
            <br />- <b>Ten en cuenta</b> que los{' '}
            <b>
              precios deben ser en <span style={{ color: 'green' }}>{data?.items?.[0]?.moneda ?? 'PESOS'}</span>
            </b>
          </Typography>
        </Box>

        {/* Datos del usuario */}
        <Grid container spacing={2} sx={{ px: 2, mb: 2 }} alignItems="center">
          <Grid size={{ xs: 12, md: 12 }}>
            <Typography>
              Señores{' '}
              <b style={{ color: '#1976d2' }}>
                {data
                  ? `${data.nombreProveedor} - ${data.codigoProveedor}`
                  : ''}
              </b>
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              En el siguiente formato se encuentra un listado con los ítems que
              solicitamos cotizar y las unidades solicitadas, por favor{' '}
              <b>diligenciar los campos</b> de los ítems que usted nos pueda
              suministrar.
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Total a Cotizar:{' '}
              <b style={{ color: '#1976d2', cursor: 'pointer' }}>
                {' '}
                {data ? `${data.items?.length || 0} Items` : 'Items'}
              </b>
            </Typography>
          </Grid>
        </Grid>

        {/* Formulario de cotización */}
        <Box sx={{ px: 2 }}>
          <Paper sx={{ p: 3, mb: 2 }}>{content}</Paper>
          
          {/* Botón de enviar cotización */}
          {!loading && data?.items && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleEnviarClick}
                disabled={isSending}
                sx={{ minWidth: 200 }}
              >
                {isSending ? 'Enviando...' : 'Enviar Cotización'}
              </Button>
            </Box>
          )}
        </Box>

        {/* Modal de confirmación para item vacío */}
        <ConfirmEmptyItemModal
          open={confirmEmptyItemOpen}
          onClose={handleCancelEmptyItem}
          onConfirm={handleConfirmEmptyItem}
        />

        {/* Snackbar para mensajes */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarMessage?.type ?? 'info'}
            sx={{ width: '100%' }}
          >
            {snackbarMessage?.text}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default App;
